(() => {
  const selector = '[data-homepage-hero]';
  const slideSelector = '[data-homepage-hero-slide]';
  const controlSelector = '[data-homepage-hero-control]';
  const initializedAttribute = 'data-homepage-hero-initialized';
  const instances = new WeakMap();

  class HomepageHero {
    constructor(element) {
      this.element = element;
      this.slides = Array.from(element.querySelectorAll(slideSelector));
      this.controls = Array.from(element.querySelectorAll(controlSelector));
      this.currentIndex = this.slides.findIndex((slide) => !slide.hidden);
      this.currentIndex = this.currentIndex >= 0 ? this.currentIndex : 0;
      this.interval = Number(element.dataset.autoplayInterval || 6000);
      this.autoplay = element.dataset.autoplay === 'true';
      this.timer = null;
      this.controlActivatedByPointer = false;
      this.swipeStart = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.fitFrame = null;
      this.scheduleFit = () => {
        window.cancelAnimationFrame(this.fitFrame);
        this.fitFrame = window.requestAnimationFrame(() => this.fitHeadings());
      };
      this.resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width;
        if (width === this.observedWidth) return;
        this.observedWidth = width;
        this.scheduleFit();
      });
      this.resizeObserver.observe(element);
      window.addEventListener('resize', this.scheduleFit);
      document.fonts?.ready.then(() => { if (element.isConnected) this.scheduleFit(); });
      this.scheduleFit();

      if (this.slides.length < 2) return;

      this.onControlClick = this.onControlClick.bind(this);
      this.onControlKeyDown = this.onControlKeyDown.bind(this);
      this.onControlPointerDown = this.onControlPointerDown.bind(this);
      this.onSwipePointerDown = this.onSwipePointerDown.bind(this);
      this.onSwipePointerCancel = this.onSwipePointerCancel.bind(this);
      this.onSwipePointerUp = this.onSwipePointerUp.bind(this);
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);

      this.controls.forEach((control) => {
        control.addEventListener('click', this.onControlClick);
        control.addEventListener('keydown', this.onControlKeyDown);
        control.addEventListener('pointerdown', this.onControlPointerDown);
      });

      element.addEventListener('mouseenter', this.pause);
      element.addEventListener('mouseleave', this.resume);
      element.addEventListener('focusin', this.pause);
      element.addEventListener('click', (event) => {
        if (event.target.closest('product-media-viewer')) this.pause();
      });
      element.addEventListener('focusout', this.resume);
      element.addEventListener('pointerdown', this.onSwipePointerDown);
      element.addEventListener('pointercancel', this.onSwipePointerCancel);
      element.addEventListener('pointerup', this.onSwipePointerUp);

      this.showSlide(this.currentIndex);
      this.resume();
    }

    fitHeadings() {
      const slide = this.slides[this.currentIndex];
      if (!slide) return;
      const context = document.createElement('canvas').getContext('2d');
      if (!context) return;
      const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      slide.querySelectorAll('.homepage-hero__heading').forEach((heading) => {
        // Measure the merchant's CSS size afresh so fitting never compounds on resize.
        heading.style.removeProperty('font-size');
        const style = getComputedStyle(heading);
        const size = parseFloat(style.fontSize);
        const width = heading.clientWidth;
        const text = heading.textContent.trim().replace(/\s+/g, ' ');
        if (!width || !text || !size || !rootSize) return;
        context.font = `${style.fontStyle} ${style.fontWeight} ${size}px ${style.fontFamily}`;
        const spacing = parseFloat(style.letterSpacing) || 0;
        const measure = (value) => context.measureText(value).width + Math.max(0, value.length - 1) * spacing;
        const longestWord = Math.max(...text.split(' ').map(measure));
        // Aim for three lines, but never sacrifice readability to force a fixed line count.
        const desired = Math.min(1, width / longestWord, width * 3 / measure(text));
        const minimum = Math.min(size, Math.max(rootSize * 1.5, size * 0.65));
        const fitted = Math.max(minimum, size * desired);
        if (fitted < size - 0.5) heading.style.fontSize = `${fitted / rootSize}rem`;
      });
    }

    destroy() {
      this.pause();
      this.resizeObserver.disconnect();
      window.removeEventListener('resize', this.scheduleFit);
      window.cancelAnimationFrame(this.fitFrame);
    }

    onControlClick(event) {
      const action = event.currentTarget.dataset.homepageHeroControl;

      if (action === 'previous') {
        this.showSlide(this.currentIndex - 1);
      } else if (action === 'next') {
        this.showSlide(this.currentIndex + 1);
      }

      this.pause();

      if (this.controlActivatedByPointer) {
        event.currentTarget.blur();
      }
    }

    onControlKeyDown() {
      this.controlActivatedByPointer = false;
    }

    onControlPointerDown() {
      this.controlActivatedByPointer = true;
    }

    onSwipePointerDown(event) {
      if (!['touch', 'pen'].includes(event.pointerType)) return;
      if (event.target.closest('a, button, input, select, textarea, summary, video, iframe, model-viewer, product-media-viewer, [role="button"]')) return;

      this.swipeStart = {
        x: event.clientX,
        y: event.clientY,
        pointerId: event.pointerId
      };
    }

    onSwipePointerCancel(event) {
      if (this.swipeStart?.pointerId !== event.pointerId) return;

      this.swipeStart = null;
    }

    onSwipePointerUp(event) {
      if (!this.swipeStart || this.swipeStart.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - this.swipeStart.x;
      const deltaY = event.clientY - this.swipeStart.y;
      this.swipeStart = null;

      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.35) return;

      this.showSlide(deltaX > 0 ? this.currentIndex - 1 : this.currentIndex + 1);
      this.pause();
    }

    showSlide(index) {
      const nextIndex = (index + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.hidden = !isActive;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });

      this.currentIndex = nextIndex;
      this.scheduleFit();
    }

    pause() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    resume() {
      if ([...this.element.querySelectorAll('product-media-viewer')].some((viewer) => viewer.active)) return;
      if (window.Shopify?.designMode || !this.autoplay || this.reducedMotion || this.timer) return;

      this.timer = window.setInterval(() => {
        this.showSlide(this.currentIndex + 1);
      }, this.interval);
    }
  }

  const elementsIn = (scope, targetSelector) => {
    const elements = Array.from(scope.querySelectorAll?.(targetSelector) || []);

    if (scope.matches?.(targetSelector)) elements.unshift(scope);

    return elements;
  };

  const init = (scope = document) => {
    elementsIn(scope, selector).forEach((element) => {
      if (element.hasAttribute(initializedAttribute)) return;

      element.setAttribute(initializedAttribute, 'true');
      instances.set(element, new HomepageHero(element));
    });
  };

  const showSelectedBlock = (event) => {
    const slide = event.target.closest?.(slideSelector);
    const hero = slide?.closest(selector);
    if (!slide || !hero) return;

    init(hero);

    const instance = instances.get(hero);
    const index = instance?.slides.indexOf(slide) ?? -1;
    if (index < 0) return;

    instance.showSlide(index);
    instance.pause();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', showSelectedBlock);
  document.addEventListener('shopify:section:unload', (event) => {
    elementsIn(event.target, selector).forEach((element) => instances.get(element)?.destroy());
  });
})();
